import { Box, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import React, { JSX } from "react";
import produce from "immer";
import { EditorState } from "prosemirror-state";
import { tl } from "../../../../components/translate";
import Can from "../../../Policy/Can";
import { RichTextfield } from "../../../../components/RichTextfield";
import {
  AssessmentSettings as IAssessmentSettings,
  ResourceCentre
} from "../../../../interfaces/ResourceCentreInterface";
import AssessmentCustomHeader from "../AssessmentCustomHeader";
import { notificationAdd } from "../../../../actions/notification";
import { useAppDispatch } from "../../../../store/hooks";
import { resolveOkhatiAiSettings } from "../../../../hooks/useOkhatiAiSettings";

interface AssessmentPrintSettingsProps {
  resourceCentre: ResourceCentre;
  onChange: (rc: ResourceCentre) => void;
  assessmentRemarksState: EditorState;
  setAssessmentRemarksState: (value: EditorState) => void;
  assessmentSettings: IAssessmentSettings;
  setAssessmentSettings: React.Dispatch<React.SetStateAction<IAssessmentSettings>>;
}

const AssessmentPrintSettings = ({
  resourceCentre,
  onChange,
  assessmentRemarksState,
  setAssessmentRemarksState,
  assessmentSettings,
  setAssessmentSettings
}: AssessmentPrintSettingsProps): JSX.Element => {
  const fileUploader = React.useRef<HTMLInputElement>(null);
  const [tempImg, setTempImg] = React.useState<string | null>(null);
  const [isCropperOpen, setIsCropperOpen] = React.useState(false);
  const dispatch = useAppDispatch();
  const [updatedHeaderImage, setUpdateHeaderImage] = React.useState(
    resourceCentre.settings.assessmentSettings.customHeaderImage
  );
  const okhatiAiSettings = resolveOkhatiAiSettings(resourceCentre);

  const handleImageChange = (e: any) => {
    if (!e?.target?.files?.length) {
      return;
    }
    const file = e.target.files[0];
    const lastDotIndex = file.name.lastIndexOf(".");
    const extension = file.name.substring(lastDotIndex + 1);

    if (file.size >= 200000 || !["jpg", "jpeg", "png", "gif"].includes(extension.toLowerCase())) {
      // show error dialog if file size exceeds 200KB
      // eslint-disable-next-line no-param-reassign
      e.target.value = "";
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "File type not valid or size is greater than 200KB.",
          autoTimeout: true
        })
      );
      return;
    }

    setTempImg(URL.createObjectURL(file));
    setIsCropperOpen(true);
  };

  return (
    <Box pl="16px">
      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={
                resourceCentre.settings.printSettings.assessmentPrintSettings.includeLetterhead
              }
              onChange={() => {
                const newRc = produce(resourceCentre, (draft) => {
                  draft.settings.printSettings.assessmentPrintSettings.includeLetterhead =
                    !resourceCentre.settings.printSettings.assessmentPrintSettings
                      .includeLetterhead;
                });
                onChange(newRc);
              }}
            />
          }
          label={tl("settings.includeLetterhead")}
        />
      </Box>
      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={resourceCentre.settings.printSettings.assessmentPrintSettings.includeFooter}
              onChange={() => {
                const newRc = produce(resourceCentre, (draft) => {
                  draft.settings.printSettings.assessmentPrintSettings.includeFooter =
                    !resourceCentre.settings.printSettings.assessmentPrintSettings.includeFooter;
                });
                onChange(newRc);
              }}
            />
          }
          label={tl("settings.includeFooter")}
        />
        {!resourceCentre.settings.printSettings.assessmentPrintSettings.includeFooter && (
          <Can policyAccessKey="deny" superAdminPass>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={
                    resourceCentre.settings.printSettings.assessmentPrintSettings
                      .hideFooterLogoCompletely
                  }
                  onChange={() => {
                    const newRc = produce(resourceCentre, (draft) => {
                      draft.settings.printSettings.assessmentPrintSettings.hideFooterLogoCompletely =
                        !resourceCentre.settings.printSettings.assessmentPrintSettings
                          .hideFooterLogoCompletely;
                    });
                    onChange(newRc);
                  }}
                />
              }
              label={tl("settings.hideFooterCompletely")}
            />
          </Can>
        )}
      </Box>
      <Box display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={
                resourceCentre.settings.printSettings.assessmentPrintSettings.showAssessmentRemarks
              }
              onChange={() => {
                const newRc = produce(resourceCentre, (draft) => {
                  draft.settings.printSettings.assessmentPrintSettings.showAssessmentRemarks =
                    !draft.settings.printSettings.assessmentPrintSettings.showAssessmentRemarks;
                });
                onChange(newRc);
              }}
            />
          }
          label={tl("settings.showAssessmentRemarks")}
        />
      </Box>
      {resourceCentre.settings.printSettings.assessmentPrintSettings.showAssessmentRemarks && (
        <Box display="flex" flexDirection="column" mt="16px" gap={1}>
          <Typography>{tl("settings.assessmentRemarks")}</Typography>
          <Box width="100%" maxWidth="400px" sx={{ overflowX: "hidden" }}>
            <RichTextfield state={assessmentRemarksState} setState={setAssessmentRemarksState} />
          </Box>
        </Box>
      )}
      {okhatiAiSettings.enabled && (
        <>
          <Box display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  data-testmation="editSummaryPrompt"
                  checked={Boolean(assessmentSettings.editSummaryPrompt)}
                  onChange={() => {
                    setAssessmentSettings((prev) => ({
                      ...prev,
                      editSummaryPrompt: !prev.editSummaryPrompt,
                      summaryPrompt: prev.summaryPrompt ?? okhatiAiSettings.prompt
                    }));
                  }}
                />
              }
              label={tl("settings.editSummaryPrompt")}
            />
          </Box>
          {assessmentSettings.editSummaryPrompt && (
            <Box display="flex" flexDirection="column" mt="16px" mb="16px" gap={1}>
              <Typography>{tl("settings.summaryPrompt")}</Typography>
              <Box width="100%" maxWidth="400px">
                <TextField
                  variant="outlined"
                  placeholder={tl("resourceCentre.okhatiAiPromptPlaceholder")}
                  multiline
                  minRows={6}
                  maxRows={12}
                  fullWidth
                  value={assessmentSettings.summaryPrompt ?? okhatiAiSettings.prompt}
                  onChange={(e) => {
                    const { value } = e.target;
                    setAssessmentSettings((prev) => ({
                      ...prev,
                      summaryPrompt: value
                    }));
                  }}
                />
              </Box>
            </Box>
          )}
        </>
      )}
      <AssessmentCustomHeader
        updatedHeaderImage={updatedHeaderImage}
        fileUploaderRef={fileUploader}
        onImageChange={handleImageChange}
        isCropperOpen={isCropperOpen}
        setIsCropperOpen={setIsCropperOpen}
        tempImg={tempImg}
        onChange={(rc) => {
          setUpdateHeaderImage(rc.settings.assessmentSettings.customHeaderImage);
          onChange(rc);
        }}
      />
    </Box>
  );
};

export default AssessmentPrintSettings;
